import { Button, Group, SimpleGrid, Stack, Text, Title } from '@mantine/core'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { ReactNode, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import { StepSection } from '@/components/deployments/deployment-editor/StepSection'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { InfiniteScroll } from '@/components/ui-shared/InfiniteScroll/InfiniteScroll'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useApplicationContext } from '@/providers/ApplicationContext'
import { useGetAppMLModels } from '@/queries/modelQueries'
import { ApplicationNestedPath, buildAppLink } from '@/router/paths'
import { ApplicationType } from '@/types/app'
import { LabelingType } from '@/types/dataset'
import { MLModel, MLModelStatus, MLModelType } from '@/types/model'
import { SelectableModelCard } from './SelectableModelCard/SelectableModelCard'

type ModelSelectionProps = {
  title: string | ReactNode
  initialSelectedModel: MLModel | null
  modelType: MLModelType
  showBackButton?: boolean
  onGoBack?: () => void
  onContinue: (selectedModel: MLModel) => void
}

export const ModelSelection = ({
  title,
  initialSelectedModel,
  modelType,
  showBackButton,
  onGoBack,
  onContinue
}: ModelSelectionProps) => {
  const navigate = useNavigate()
  const { application } = useApplicationContext()

  const [selectedModel, setSelectedModel] = useState<MLModel | null>(
    initialSelectedModel
  )

  const { data, isFetching, isError, refetch, fetchNextPage, hasNextPage } =
    useGetAppMLModels(application?.id || '', {
      status: [MLModelStatus.PreTrained, MLModelStatus.Trained],
      model_type: modelType
    })

  const models = data?.pages.flatMap((page) => page.results) ?? []

  const handleLoadMore = () => {
    void fetchNextPage()
  }

  const handleOnReload = () => {
    void refetch()
  }

  const toggleModelSelection = (model: MLModel) => {
    setSelectedModel((prev) => (prev?.id === model.id ? null : model))
  }

  const handleGoToModels = () => {
    if (!application) {
      return
    }

    void navigate(buildAppLink(application.id, ApplicationNestedPath.models))
  }

  const handleContinueClick = () => {
    if (selectedModel) {
      onContinue(selectedModel)
    }
  }

  if (isError) {
    return (
      <ErrorWithReload onReload={handleOnReload}>
        <FormattedMessage id="models.modelList.apiError" />
      </ErrorWithReload>
    )
  }

  return (
    <>
      <Group align="center" gap="lg" mb="md">
        <Title order={3}>{title}</Title>
      </Group>

      <StepSection
        actions={
          <Group>
            {showBackButton && (
              <Button
                size="md"
                variant="outline"
                miw={200}
                leftSection={<IconChevronLeft size={16} stroke={3} />}
                onClick={onGoBack}
              >
                <FormattedMessage id="deployments.previousStep" />
              </Button>
            )}

            <Button
              miw={200}
              size="md"
              rightSection={<IconChevronRight size={16} stroke={3} />}
              disabled={selectedModel === null}
              onClick={handleContinueClick}
            >
              <FormattedMessage id="deployments.nextStep" />
            </Button>
          </Group>
        }
      >
        {models.length === 0 && !isFetching && (
          <Stack mih={200} align="center" justify="center">
            <Text size="md" fs="italic">
              <FormattedMessage
                id="deployments.editor.noModels"
                values={{ appName: application?.name }}
              />
            </Text>

            <Button size="sm" variant="outline" onClick={handleGoToModels}>
              <FormattedMessage id="deployments.editor.viewModels" />
            </Button>
          </Stack>
        )}

        {selectedModel?.labeling_type === LabelingType.Classification && (
          <PlatformAlert variant="info" mb="xl">
            <FormattedMessage id="models.classification.deviceSupport.info" />
          </PlatformAlert>
        )}

        <InfiniteScroll
          hasMore={hasNextPage}
          isFetching={isFetching}
          onLoadMore={handleLoadMore}
        >
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing="xl">
            {models.map((model) => (
              <SelectableModelCard
                key={model.id}
                model={model}
                applicationType={
                  application?.application_type || ApplicationType.SingleStage
                }
                isSelected={selectedModel?.id === model.id}
                onClick={() => toggleModelSelection(model)}
              />
            ))}
          </SimpleGrid>
        </InfiniteScroll>
      </StepSection>
    </>
  )
}
